import React from "react";
import { Play, Sparkles } from "lucide-react";

const SeedsOfWisdom = () => {
  return (
    <section id="seeds-of-wisdom" className="py-24 bg-gradient-to-br from-gray-900 via-purple-900 to-black relative overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-0 left-0 w-full h-full bg-gradient-to-br from-yellow-500 via-orange-500 to-purple-600" />
        <svg className="absolute inset-0 w-full h-full" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="grid" width="40" height="40" patternUnits="userSpaceOnUse">
              <path d="M 40 0 L 0 0 0 40" fill="none" stroke="white" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#grid)" />
        </svg>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center space-x-3 mb-4">
            <Sparkles className="text-yellow-400" size={32} />
            <Play className="text-orange-500" size={32} />
            <Sparkles className="text-yellow-400" size={32} />
          </div>
          
          <h2 className="text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-white mb-6">
            Seeds of <span className="bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 bg-clip-text text-transparent font-normal">Wisdom</span>
          </h2>
          
          <p className="text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed font-light">
            A visual meditation on planting seeds of divine truth. Watch as the journey unfolds—
            each frame a testament to faith, growth, and the harvest of spiritual abundance.
          </p>
        </div>

        {/* Video Container */}
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-2xl overflow-hidden shadow-2xl border-4 border-gradient-to-r from-yellow-400 via-orange-500 to-purple-600">
            {/* Decorative Corner Accents */}
            <div className="absolute top-0 left-0 w-20 h-20 border-t-4 border-l-4 border-yellow-400 rounded-tl-2xl z-10" />
            <div className="absolute top-0 right-0 w-20 h-20 border-t-4 border-r-4 border-orange-400 rounded-tr-2xl z-10" />
            <div className="absolute bottom-0 left-0 w-20 h-20 border-b-4 border-l-4 border-purple-400 rounded-bl-2xl z-10" />
            <div className="absolute bottom-0 right-0 w-20 h-20 border-b-4 border-r-4 border-red-400 rounded-br-2xl z-10" />

            {/* Video Player */}
            <div className="relative aspect-video bg-black">
              <video
                controls
                preload="metadata"
                className="w-full h-full"
                poster="https://customer-assets.emergentagent.com/job_aligned-kotf/artifacts/7cpvewhu_Yahffirmations%20-%20KOTF888.png"
              >
                <source 
                  src="https://customer-assets.emergentagent.com/job_ct-digital-hub/artifacts/zxwbwppj_KOTF%20-%20Seeds.mp4" 
                  type="video/mp4" 
                />
                Your browser does not support the video tag. Please use a modern browser to view this content.
              </video>
            </div>

            {/* Glow Effect */}
            <div className="absolute inset-0 border-4 border-gradient-to-r from-yellow-400 via-orange-500 to-purple-600 rounded-2xl pointer-events-none opacity-50 blur-xl" />
          </div>

          {/* Video Description */}
          <div className="mt-12 bg-gradient-to-br from-purple-900/50 to-black/50 backdrop-blur-sm rounded-xl p-8 border border-purple-500/30">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="w-16 h-16 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">🌱</span>
                </div>
                <h3 className="text-lg font-medium text-white mb-2">Plant</h3>
                <p className="text-gray-300 text-sm font-light">
                  Every seed planted in the fine soil with FLY FREQUENCY (Faith | Love | Yah in me) carries the promise of abundant harvest
                </p>
              </div>

              <div>
                <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-red-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">🔥</span>
                </div>
                <h3 className="text-lg font-medium text-white mb-2">Nurture</h3>
                <p className="text-gray-300 text-sm font-light">
                  Through trials and tribulations, the fiyah of YAH refines and strengthens
                </p>
              </div>

              <div>
                <div className="w-16 h-16 bg-gradient-to-br from-purple-500 to-indigo-500 rounded-full mx-auto mb-4 flex items-center justify-center">
                  <span className="text-2xl">✨</span>
                </div>
                <h3 className="text-lg font-medium text-white mb-2">Harvest</h3>
                <p className="text-gray-300 text-sm font-light">
                  Reap the rewards of divine wisdom manifested through YAH, HEALTH, WEALTH, HAPPINESS The Universal Pillars of Success
                </p>
              </div>
            </div>

            {/* Quote */}
            <div className="mt-8 pt-8 border-t border-purple-500/30">
              <blockquote className="text-center">
                <p className="text-lg text-gray-200 italic font-light mb-3">
                  "The kingdom of heavens is like a mustard grain, that a man took and planted in his field. 
                  It is in fact the tiniest of all the seeds, but when it has grown, it is the largeset of the vegetable plants and becomes a tree, so that the birds of heaven come and find lodging among its branches."
                </p>
                <footer className="text-yellow-400 text-sm font-medium">
                  — Matthew 13:31-32
                </footer>
              </blockquote>
            </div>
          </div>

          {/* Call to Action */}
          <div className="mt-8 text-center">
            <p className="text-gray-300 font-light mb-4">
              Ready to plant your seeds of transformation?
            </p>
            <a
              href="#contact"
              className="inline-block px-8 py-4 bg-gradient-to-r from-yellow-400 via-orange-500 to-red-500 text-white rounded-full font-medium tracking-wide hover:shadow-xl transition-all hover:scale-105 transform duration-300"
              onClick={(e) => {
                e.preventDefault();
                document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' });
              }}
            >
              Let's Connect
            </a>
          </div>
        </div>
      </div>

      {/* Sacred Symbols Floating */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 text-4xl text-yellow-400/20 animate-pulse" style={{animationDuration: '3s'}}>
          888
        </div>
        <div className="absolute bottom-32 right-20 text-3xl text-orange-400/20 animate-bounce" style={{animationDuration: '4s'}}>
          ✨
        </div>
        <div className="absolute top-1/2 right-10 text-2xl text-purple-400/20 animate-pulse" style={{animationDuration: '5s'}}>
          🌱
        </div>
      </div>
    </section>
  );
};

export default SeedsOfWisdom;
